import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Brain, Send, User } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ChatWithNova = () => {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (companyId) {
      fetchChatHistory();
    } else {
      setLoadingHistory(false);
    }
  }, [companyId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const fetchChatHistory = async () => {
    try {
      const response = await axios.get(`${API}/chat/${companyId}`);
      setMessages(response.data);
      setLoadingHistory(false);
    } catch (error) {
      console.error("Error fetching chat history:", error);
      setLoadingHistory(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim()) return;

    const tempCompanyId = companyId || "guest-" + Date.now();

    setLoading(true);
    const userMessage = inputMessage;
    setInputMessage("");

    try {
      const response = await axios.post(`${API}/chat`, {
        company_id: tempCompanyId,
        message: userMessage
      });

      setMessages([...messages, response.data.user_message, response.data.nova_response]);
    } catch (error) {
      console.error("Error sending message:", error);
      toast({
        title: "Error",
        description: "Failed to send message",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-white">
      {/* Header */}
      <header className="border-b bg-white/70 backdrop-blur-xl shadow-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <Button variant="ghost" onClick={() => navigate("/")} data-testid="back-home-chat-btn">
            &larr; Home
          </Button>
          <div className="flex items-center gap-2">
            <Brain className="h-6 w-6 text-green-600" />
            <h1 className="text-xl font-bold">Chat with Nova</h1>
          </div>
          <div className="w-20"></div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <Card className="h-[calc(100vh-200px)] flex flex-col" data-testid="chat-container">
          <CardHeader className="border-b">
            <CardTitle className="flex items-center gap-2">
              <Brain className="h-6 w-6 text-green-600" />
              Nova - Your AI Advisor
            </CardTitle>
          </CardHeader>
          <CardContent className="flex-1 overflow-y-auto p-4 space-y-4" data-testid="chat-messages">
            {loadingHistory ? (
              <div className="flex justify-center items-center h-full">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
              </div>
            ) : messages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center" data-testid="welcome-message">
                <Brain className="h-16 w-16 text-green-600 mb-4" />
                <h3 className="text-xl font-bold mb-2">Welcome! I'm Nova</h3>
                <p className="text-gray-600 max-w-md">
                  I'm your AI advisor. Ask me anything about startups, fundraising, product strategy, growth, or anything else!
                </p>
              </div>
            ) : (
              messages.map((msg, idx) => (
                <div
                  key={msg.id || idx}
                  className={`flex gap-3 ${msg.sender === "user" ? "justify-end" : "justify-start"}`}
                  data-testid={`message-${msg.sender}-${idx}`}
                >
                  {msg.sender === "nova" && (
                    <Avatar className="h-8 w-8 bg-green-600">
                      <AvatarFallback>
                        <Brain className="h-4 w-4 text-white" />
                      </AvatarFallback>
                    </Avatar>
                  )}
                  <div
                    className={`max-w-[70%] rounded-lg p-3 ${
                      msg.sender === "user"
                        ? "bg-green-600 text-white"
                        : "bg-gray-100 text-gray-900"
                    }`}
                  >
                    <p className="whitespace-pre-wrap" data-testid={`message-content-${idx}`}>{msg.message}</p>
                  </div>
                  {msg.sender === "user" && (
                    <Avatar className="h-8 w-8 bg-gray-600">
                      <AvatarFallback>
                        <User className="h-4 w-4 text-white" />
                      </AvatarFallback>
                    </Avatar>
                  )}
                </div>
              ))
            )}
            {loading && (
              <div className="flex gap-3 justify-start" data-testid="nova-typing-indicator">
                <Avatar className="h-8 w-8 bg-green-600">
                  <AvatarFallback>
                    <Brain className="h-4 w-4 text-white" />
                  </AvatarFallback>
                </Avatar>
                <div className="bg-gray-100 rounded-lg p-3">
                  <div className="flex gap-1">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "0.1s" }}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "0.2s" }}></div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </CardContent>
          <div className="border-t p-4">
            <form onSubmit={handleSendMessage} className="flex gap-2">
              <Input
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                placeholder="Ask Nova anything..."
                disabled={loading}
                className="flex-1"
                data-testid="chat-input"
              />
              <Button
                type="submit"
                disabled={loading || !inputMessage.trim()}
                className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 shadow-lg hover:scale-105 transition-all duration-300"
                data-testid="send-message-btn"
              >
                <Send className="h-4 w-4" />
              </Button>
            </form>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default ChatWithNova;